export const ASN_DELIVERY_TYPES = {
  direct: "direct",
  courier: "courier",
};

export const ASN_DOCUMENT_TYPES = {
  ewayBill: "ewb_doc",
  courierReceipt: "courier_doc",
};

export const ASN_TRANSPORTERS = [
  "TCI Express",
  "OM Logistics",
  "Delhivery",
  "V-Express",
  "Blue Dart",
  "Scorpion Express",
];

export const MAX_ASN_DOCUMENT_SIZE_BYTES = 5 * 1024 * 1024;

const normalizeText = (value) =>
  typeof value === "string" ? value.trim() : "";

const SHIPPING_NOTE_DOCUMENT_TYPES = new Set([
  "asn",
  "asn_doc",
  "asn_document",
  "advance_shipping_note",
  "shipping_note",
  "shipping_note_doc",
]);

export const getCreatedShippingNoteDocument = (asnInfo = {}) => {
  const documents = Array.isArray(asnInfo?.asn_documents)
    ? asnInfo.asn_documents
    : [];

  return (
    documents.find((document) => {
      const documentType = normalizeText(document?.document_type).toLowerCase();
      const documentPath = normalizeText(document?.document_path);

      return (
        Boolean(documentPath) &&
        (SHIPPING_NOTE_DOCUMENT_TYPES.has(documentType) ||
          (documentType.includes("shipping") && documentType.includes("note")))
      );
    }) || null
  );
};

export const getSelectedShippingNoteInvoices = (
  scheduleGroup = {},
  selectedInvoiceIds = [],
  currentAsnId = 0
) => {
  const invoiceIdSet = new Set(
    selectedInvoiceIds
      .map(Number)
      .filter((invoiceId) => Number.isInteger(invoiceId) && invoiceId > 0)
  );
  const normalizedCurrentAsnId = Number(currentAsnId) || 0;
  const selectedInvoices = [];

  const purchaseOrders = Array.isArray(scheduleGroup?.purchase_orders)
    ? scheduleGroup.purchase_orders
    : [];

  purchaseOrders.forEach((purchaseOrder) => {
    const invoices = Array.isArray(purchaseOrder.invoices)
      ? purchaseOrder.invoices
      : [];

    invoices.forEach((invoice) => {
      const invoiceAsnId = Number(invoice.asn_id);
      const isAvailable =
        !(invoiceAsnId > 0) || invoiceAsnId === normalizedCurrentAsnId;

      if (invoiceIdSet.has(Number(invoice.vendor_invoice_id)) && isAvailable) {
        selectedInvoices.push({
          ...invoice,
          purchase_order_id: purchaseOrder.purchase_order_id,
          purchase_order_number: purchaseOrder.purchase_order_number,
          schedule_id: purchaseOrder.schedule_id,
        });
      }
    });
  });

  return selectedInvoices;
};

export const getShippingNoteSummary = (selectedInvoices = []) => {
  const normalizedInvoices = Array.isArray(selectedInvoices)
    ? selectedInvoices
    : [];
  const purchaseOrderIds = new Set();

  const summary = normalizedInvoices.reduce(
    (result, invoice) => {
      const purchaseOrderId = Number(invoice.purchase_order_id);
      if (purchaseOrderId > 0) purchaseOrderIds.add(purchaseOrderId);

      result.skuCount += Number(invoice.number_of_skus) || 0;
      result.weightInKg += Number(invoice.weight_in_kg) || 0;
      result.totalAmount += Number(invoice.invoice_amount) || 0;
      return result;
    },
    {
      invoiceCount: normalizedInvoices.length,
      poCount: 0,
      skuCount: 0,
      weightInKg: 0,
      totalAmount: 0,
    }
  );

  summary.poCount = purchaseOrderIds.size;
  return summary;
};

export const isValidVehicleNumber = (value) => {
  const normalizedValue = normalizeText(value).toUpperCase();
  return /^(([A-Z]{2}\s?[0-9]{2}\s?(?:[A-Z]{1,3}\s?)?[0-9]{4})|([0-9]{2}\s?BH\s?[0-9]{4}\s?[A-Z]{1,2})|([A-Z]{2}\s?VA\s?[A-Z]{2}\s?[0-9]{4})|([0-9]{2}\s?[A-Z]\s?[0-9]{6}\s?[A-Z])|([0-9]{1,3}\s?(?:CD|CC|UN)\s?[0-9]{1,4}))$/i.test(normalizedValue);
};

export const isValidDriverPhone = (value) => /^[6-9]\d{9}$/.test(value || "");

export const isValidDriverName = (value) =>
  /^[A-Za-z][A-Za-z ]{1,49}$/.test(normalizeText(value));

export const validateShippingNoteForm = ({
  deliveryType,
  ewayBillFiles = [],
  ewayBillNumber,
  courierReceiptFiles = [],
  docketNumber,
  transporterName,
  vehicleNumber,
  driverPhone,
  driverName,
} = {}) => {
  const errors = {};

  if (normalizeText(ewayBillNumber) && ewayBillFiles.length === 0) {
    errors.ewayBillFiles = "Upload the e-way bill for this number.";
  }

  if (deliveryType === ASN_DELIVERY_TYPES.direct) {
    if (!isValidVehicleNumber(vehicleNumber)) {
      errors.vehicleNumber = "Enter a valid vehicle number.";
    }
    if (!isValidDriverPhone(driverPhone)) {
      errors.driverPhone = "Enter a valid 10-digit mobile number.";
    }
    if (!isValidDriverName(driverName)) {
      errors.driverName = "Enter a valid driver name using letters only.";
    }
  } else if (deliveryType === ASN_DELIVERY_TYPES.courier) {
    if (courierReceiptFiles.length === 0) {
      errors.courierReceiptFiles = "Upload the docket receipt.";
    }
    if (!normalizeText(docketNumber)) {
      errors.docketNumber = "Enter the docket number.";
    }
    if (!ASN_TRANSPORTERS.includes(transporterName)) {
      errors.transporterName = "Select a transporter.";
    }
    if (normalizeText(vehicleNumber) && !isValidVehicleNumber(vehicleNumber)) {
      errors.vehicleNumber = "Enter a valid vehicle number.";
    }
  } else {
    errors.deliveryType = "Select a delivery type.";
  }

  return errors;
};

const buildAsnInfo = ({
  deliveryType,
  ewayBillPath = "",
  ewayBillNumber = "",
  courierReceiptPath = "",
  docketNumber = "",
  transporterName = "",
  vehicleNumber = "",
  driverPhone = "",
  driverName = "",
} = {}) => {
  const asnDocuments = [];
  const normalizedEwayBillPath = normalizeText(ewayBillPath);
  const normalizedCourierReceiptPath = normalizeText(courierReceiptPath);

  if (normalizedEwayBillPath) {
    asnDocuments.push({
      entity_type: "asn",
      document_type: ASN_DOCUMENT_TYPES.ewayBill,
      document_path: normalizedEwayBillPath,
      document_number: normalizeText(ewayBillNumber),
    });
  }

  if (
    deliveryType === ASN_DELIVERY_TYPES.courier &&
    normalizedCourierReceiptPath
  ) {
    asnDocuments.push({
      entity_type: "asn",
      document_type: ASN_DOCUMENT_TYPES.courierReceipt,
      document_path: normalizedCourierReceiptPath,
      document_number: normalizeText(docketNumber),
    });
  }

  const deliveryDetails = {
    delivery_type: deliveryType,
    vehicle_number: normalizeText(vehicleNumber),
    da_name:
      deliveryType === ASN_DELIVERY_TYPES.direct
        ? normalizeText(driverName)
        : normalizeText(transporterName),
  };

  if (deliveryType === ASN_DELIVERY_TYPES.direct) {
    deliveryDetails.da_identifier_type = "phone_number";
    deliveryDetails.da_identifier_value = normalizeText(driverPhone);
  }

  return {
    asn_documents: asnDocuments,
    delivery_details: deliveryDetails,
  };
};

export const buildCreateAdvanceShippingNoteRequest = ({
  selectedInvoices = [],
  ...asnInfoFields
} = {}) => ({
  invoices: selectedInvoices.map((invoice) => ({
    vendor_invoice_id: Number(invoice.vendor_invoice_id),
    invoice_number: String(invoice.invoice_number || ""),
    action: "UPSERT",
  })),
  asn_info: buildAsnInfo(asnInfoFields),
});

export const buildUpdateAdvanceShippingNoteRequest = ({
  asnId,
  selectedInvoices = [],
  removedInvoices = [],
  ...asnInfoFields
} = {}) => ({
  asn_id: Number(asnId),
  invoices: [
    ...selectedInvoices.map((invoice) => ({
      vendor_invoice_id: Number(invoice.vendor_invoice_id),
      invoice_number: String(invoice.invoice_number || ""),
      action: "UPSERT",
    })),
    ...removedInvoices.map((invoice) => ({
      vendor_invoice_id: Number(invoice.vendor_invoice_id),
      invoice_number: String(invoice.invoice_number || ""),
      action: "DELETE",
    })),
  ],
  asn_info: buildAsnInfo(asnInfoFields),
});
